import React, { useState, useEffect, useMemo } from 'react';
import { TOCItem } from './BlogComponents/TOCItem';
import { BlogContent } from '@/data/blogPosts';

interface TOCEntry {
  id: string;
  label: string;
  level: number;
}

interface TableOfContentsProps {
  content: BlogContent[];
}

const TableOfContents: React.FC<TableOfContentsProps> = ({ content }) => {
  const [activeId, setActiveId] = useState<string>('');

  // Extract TOC entries from content
  const tocEntries: TOCEntry[] = useMemo(
    () =>
      content
        .filter(
          (block) =>
            ['heading1', 'heading2', 'heading3'].includes(block.type) && block.id,
        )
        .map((block) => ({
          id: block.id as string,
          label: block.content,
          level: parseInt(block.type.replace('heading', ''), 10),
        })),
    [content],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px' },
    );

    // Observe all section headings
    tocEntries.forEach((toc) => {
      const element = document.getElementById(toc.id);
      if (element) observer.observe(element);
    });

    return () => {
      tocEntries.forEach((toc) => {
        const element = document.getElementById(toc.id);
        if (element) observer.unobserve(element);
      });
    };
  }, [tocEntries]);

  return (
    <div className="max-h-[calc(100vh-120px)] overflow-y-auto pr-4">
      <h4 className="text-lg mb-4 dark:text-white">Table of Contents</h4>
      <nav className="space-y-1">
        {tocEntries.map((entry, index) => (
          <TOCItem
            key={index}
            id={entry.id}
            label={entry.label}
            level={entry.level}
            activeId={activeId}
          />
        ))}
      </nav>
    </div>
  );
};

export default TableOfContents;
